<template>
  <div class="transaction-history space-y-4">
    <!-- Header -->
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-lg font-bold text-gray-900">Historique des transactions</h3>
        <p class="text-sm text-gray-600 mt-1">Vos dernières opérations</p>
      </div>
      <button
        v-if="transactions.length > 0"
        type="button"
        class="text-sm text-blue-600 font-medium hover:text-blue-700"
        @click="handleRefresh"
      >
        Actualiser
      </button>
    </div>

    <!-- Filters -->
    <div class="flex gap-2 overflow-x-auto pb-2">
      <button
        v-for="filter in filters"
        :key="filter.id"
        type="button"
        class="px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors"
        :class="activeFilter === filter.id ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'"
        @click="setActiveFilter(filter.id)"
      >
        {{ filter.label }}
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="space-y-3">
      <div v-for="i in 5" :key="i" class="bg-white rounded-xl p-4 animate-pulse">
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-gray-200 rounded-full"></div>
          <div class="flex-1 space-y-2">
            <div class="h-4 bg-gray-200 rounded w-1/2"></div>
            <div class="h-3 bg-gray-200 rounded w-1/3"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else-if="filteredTransactions.length === 0" class="text-center py-12">
      <div class="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
        <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      </div>
      <h4 class="text-lg font-semibold text-gray-900">Aucune transaction</h4>
      <p class="text-sm text-gray-600 mt-1">
        {{ activeFilter === 'all' ? 'Vous n\'avez aucune transaction pour le moment.' : `Aucune transaction de type ${getFilterLabel(activeFilter)}.` }}
      </p>
    </div>

    <!-- Transaction List -->
    <div v-else class="space-y-3">
      <div
        v-for="transaction in paginatedTransactions"
        :key="transaction.id"
        class="bg-white rounded-xl p-4 border border-gray-100 hover:border-gray-200 transition-colors cursor-pointer"
        @click="handleTransactionClick(transaction)"
      >
        <div class="flex items-center gap-3">
          <!-- Icon -->
          <div
            class="w-10 h-10 rounded-full flex items-center justify-center"
            :class="getTransactionIconClass(transaction)"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path v-if="transaction.type === 'credit'" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </div>

          <!-- Transaction Info -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center justify-between">
              <h4 class="font-medium text-gray-900 truncate">{{ transaction.description }}</h4>
              <span
                class="text-sm font-bold"
                :class="transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'"
              >
                {{ transaction.type === 'credit' ? '+' : '-' }}{{ formatAmount(transaction.amount) }}
              </span>
            </div>
            <div class="flex items-center justify-between mt-1">
              <div class="flex items-center gap-2">
                <span class="text-xs text-gray-500">{{ formatDate(transaction.createdAt) }}</span>
                <span class="text-xs px-2 py-0.5 rounded-full" :class="getStatusClass(transaction.status)">
                  {{ getStatusLabel(transaction.status) }}
                </span>
              </div>
              <span class="text-xs text-gray-500">{{ transaction.paymentMethod || 'Wallet' }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="flex items-center justify-center gap-2 pt-4">
      <button
        type="button"
        class="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
        :disabled="currentPage === 1"
        @click="goToPage(currentPage - 1)"
      >
        Précédent
      </button>
      <div class="flex gap-1">
        <button
          v-for="page in visiblePages"
          :key="page"
          type="button"
          class="w-10 h-10 rounded-lg text-sm font-medium transition-colors"
          :class="currentPage === page ? 'bg-blue-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'"
          @click="goToPage(page)"
        >
          {{ page }}
        </button>
      </div>
      <button
        type="button"
        class="px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
        :disabled="currentPage === totalPages"
        @click="goToPage(currentPage + 1)"
      >
        Suivant
      </button>
    </div>

    <!-- VBusHelper for event handling -->
    <VBusHelper
      :emit-on-mounted="'WALLET.TRANSACTIONS_LOAD'"
      :data-on-mounted="{ page: 1, limit: itemsPerPage }"
    />
    <VBusHelper
      :react-on="'WALLET.TRANSACTIONS_UPDATED'"
      :handler="handleTransactionsUpdated"
    />
  </div>
</template>

<script>
export default {
  name: 'TransactionHistory',

  props: {
    transactions: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      activeFilter: 'all',
      currentPage: 1,
      itemsPerPage: 10,
      filters: [
        { id: 'all', label: 'Toutes' },
        { id: 'credit', label: 'Dépôts' },
        { id: 'debit', label: 'Retraits' },
        { id: 'completed', label: 'Réussies' },
        { id: 'pending', label: 'En cours' },
        { id: 'failed', label: 'Échouées' }
      ]
    };
  },

  computed: {
    filteredTransactions() {
      let filtered = [...this.transactions];

      switch (this.activeFilter) {
        case 'credit':
          return filtered.filter(t => t.type === 'credit');
        case 'debit':
          return filtered.filter(t => t.type === 'debit');
        case 'completed':
          return filtered.filter(t => t.status === 'completed');
        case 'pending':
          return filtered.filter(t => t.status === 'pending');
        case 'failed':
          return filtered.filter(t => t.status === 'failed');
        default:
          return filtered;
      }
    },

    totalPages() {
      return Math.ceil(this.filteredTransactions.length / this.itemsPerPage);
    },

    paginatedTransactions() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredTransactions.slice(start, end);
    },

    visiblePages() {
      const pages = [];
      const maxVisible = 5;

      if (this.totalPages <= maxVisible) {
        for (let i = 1; i <= this.totalPages; i++) {
          pages.push(i);
        }
      } else {
        let start = Math.max(1, this.currentPage - 2);
        let end = Math.min(this.totalPages, start + maxVisible - 1);

        if (end - start < maxVisible - 1) {
          start = Math.max(1, end - maxVisible + 1);
        }

        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
      }

      return pages;
    }
  },

  methods: {
    getFilterLabel(filterId) {
      const filter = this.filters.find(f => f.id === filterId);
      return filter ? filter.label.toLowerCase() : filterId;
    },

    setActiveFilter(filterId) {
      this.activeFilter = filterId;
      this.currentPage = 1;
      this.$bus.emit('WALLET.TRANSACTIONS_FILTER_CHANGE', {
        filter: filterId,
        timestamp: Date.now()
      });
    },

    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.$bus.emit('WALLET.TRANSACTIONS_PAGE_CHANGE', {
          page,
          limit: this.itemsPerPage,
          timestamp: Date.now()
        });
      }
    },

    handleRefresh() {
      this.currentPage = 1;
      this.$bus.emit('WALLET.TRANSACTIONS_REFRESH', {
        timestamp: Date.now()
      });
    },

    handleTransactionClick(transaction) {
      this.$bus.emit('WALLET.TRANSACTION_DETAIL', {
        transactionId: transaction.id,
        timestamp: Date.now()
      });
    },

    handleTransactionsUpdated(data) {
      if (data.resetPage) {
        this.currentPage = 1;
      }
    },

    getTransactionIconClass(transaction) {
      if (transaction.status === 'completed') {
        return transaction.type === 'credit' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600';
      } else if (transaction.status === 'pending') {
        return 'bg-yellow-100 text-yellow-600';
      } else {
        return 'bg-gray-100 text-gray-600';
      }
    },

    getStatusClass(status) {
      switch (status) {
        case 'completed':
          return 'bg-green-100 text-green-700';
        case 'pending':
          return 'bg-yellow-100 text-yellow-700';
        case 'failed':
          return 'bg-red-100 text-red-700';
        default:
          return 'bg-gray-100 text-gray-700';
      }
    },

    getStatusLabel(status) {
      switch (status) {
        case 'completed':
          return 'Réussie';
        case 'pending':
          return 'En cours';
        case 'failed':
          return 'Échouée';
        default:
          return status;
      }
    },

    formatAmount(amount) {
      return new Intl.NumberFormat('fr-FR', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    },

    formatDate(date) {
      const d = new Date(date);
      const now = new Date();
      const diff = Math.floor((now - d) / 1000);

      if (diff < 60) return "À l'instant";
      if (diff < 3600) return `Il y a ${Math.floor(diff / 60)} min`;
      if (diff < 86400) return `Il y a ${Math.floor(diff / 3600)}h`;
      if (diff < 604800) return `Il y a ${Math.floor(diff / 86400)}j`;

      return d.toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'short',
        year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  }
};
</script>

<style scoped>
.transaction-history {
  max-height: 600px;
  overflow-y: auto;
}
</style>
